import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TranslocoRootModule } from 'app/transloco-root.module';
import { IgntrIconComponent } from 'app/shared/igntr';
import { DashboardCardComponent } from './dashboard-card.component';
import { NewsUserPageListDto } from 'app/api/models/news/newsUserPageListDto';

@Component({
  selector: 'app-dashboard-news-feed',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoRootModule,
    IgntrIconComponent,
    DashboardCardComponent,
  ],
  template: `
    <app-dashboard-card
      [title]="'dashNewsTitle' | transloco"
      [sub]="'dashNewsSub' | transloco"
    >
      <a
        card-action
        routerLink="/posts"
        class="text-[14px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
      >
        {{ 'dashSeeAll' | transloco }}
        <igntr-icon name="arrowR" [size]="14"></igntr-icon>
      </a>

      @if (loading) {
        <div class="text-[14px] text-slate-400 py-6 text-center">
          {{ 'dashLoading' | transloco }}
        </div>
      } @else if (items.length === 0) {
        <div class="text-[14px] text-slate-400 py-6 text-center">
          {{ 'dashNewsEmpty' | transloco }}
        </div>
      } @else {
        <div class="space-y-[10px]">
          @for (n of items; track n.id) {
            <a
              [routerLink]="['/posts', n.id]"
              class="group w-full text-left rounded-xl bg-white hover:shadow-[0_10px_26px_-18px_rgba(15,23,42,.3)] transition-all p-4 flex gap-[14px]"
              style="border: 1px solid rgba(226,232,240,0.7);"
            >
              <div
                class="w-12 h-12 rounded-lg shrink-0 overflow-hidden bg-slate-100 flex items-center justify-center text-slate-400"
              >
                @if (previewUrl(n); as url) {
                  <img
                    [src]="url"
                    [alt]="n.title"
                    class="w-full h-full object-cover"
                  />
                } @else {
                  <igntr-icon name="scroll" [size]="20"></igntr-icon>
                }
              </div>
              <div class="flex-1 min-w-0">
                <div
                  class="flex items-center gap-1.5 mb-1 text-[14px] text-slate-500"
                >
                  @if (n.userName) {
                    <span class="truncate font-medium text-slate-600">
                      {{ n.userName }}
                    </span>
                    <span class="text-slate-300">·</span>
                  }
                  <span class="text-slate-400">
                    {{ formatTime(n.createdAt) }}
                  </span>
                  @if (!n.isRead) {
                    <span
                      class="ml-auto w-1.5 h-1.5 rounded-full bg-accent shrink-0"
                      [attr.aria-label]="'dashNewsUnread' | transloco"
                    ></span>
                  }
                </div>
                <div
                  [class]="
                    'text-[14px] text-slate-900 leading-snug line-clamp-2 ' +
                    (n.isRead ? 'font-medium' : 'font-semibold')
                  "
                >
                  {{ n.title }}
                </div>
                @if (n.description) {
                  <div
                    class="mt-1 text-[14px] text-slate-500 leading-relaxed line-clamp-2"
                  >
                    {{ stripHtml(n.description) }}
                  </div>
                }
              </div>
            </a>
          }
        </div>
      }
    </app-dashboard-card>
  `,
})
export class DashboardNewsFeedComponent {
  @Input() news: NewsUserPageListDto[] = [];
  @Input() loading = false;

  get items(): NewsUserPageListDto[] {
    return (this.news ?? []).slice(0, 4);
  }

  previewUrl(n: NewsUserPageListDto): string | null {
    return n.previewMediaLibraryImage?.fileUrl || null;
  }

  formatTime(date: Date | string): string {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return '';
    const diffMs = Date.now() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0)
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (diffDays === 1) return '1d';
    if (diffDays < 7) return `${diffDays}d`;
    return d.toLocaleDateString();
  }

  stripHtml(html: string): string {
    return html
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }
}
